import axios from 'axios';
import { getCsrfToken } from '../lib/auth';
import { notifyUnauthorized } from '../lib/auth-events';
import { getDevIdentityHeaders } from '../lib/dev-auth';

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  // Send the m365 session cookie on every request (same-origin /api proxy).
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// PascalCase ↔ camelCase conversion
// The .NET backend uses PascalCase globally (Teams bot compatibility).
// The React dashboard expects camelCase. These interceptors bridge the gap.

// Properties whose values are dictionaries with data keys (not property names).
// These keys should NOT be transformed — they are identifiers like product IDs.
const DICTIONARY_PROPERTIES_CAMEL = new Set([
  'productRatePlanIds', 'metadata', 'settings', 'quotas',
]);
const DICTIONARY_PROPERTIES_PASCAL = new Set([
  'ProductRatePlanIds', 'Metadata', 'Settings', 'Quotas',
]);

function toCamelCase(obj: unknown, isDictionaryValue = false): unknown {
  if (Array.isArray(obj)) return obj.map(item => toCamelCase(item, false));
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    // If this object is a dictionary value, preserve its keys as-is
    // but still recurse into its values
    if (isDictionaryValue) {
      return Object.keys(obj as Record<string, unknown>).reduce((acc, key) => {
        acc[key] = toCamelCase((obj as Record<string, unknown>)[key], false);
        return acc;
      }, {} as Record<string, unknown>);
    }
    return Object.keys(obj as Record<string, unknown>).reduce((acc, key) => {
      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      const isDict = DICTIONARY_PROPERTIES_CAMEL.has(camelKey);
      acc[camelKey] = toCamelCase((obj as Record<string, unknown>)[key], isDict);
      return acc;
    }, {} as Record<string, unknown>);
  }
  return obj;
}

function toPascalCase(obj: unknown, isDictionaryValue = false): unknown {
  if (Array.isArray(obj)) return obj.map(item => toPascalCase(item, false));
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    // If this object is a dictionary value, preserve its keys as-is
    // but still recurse into its values
    if (isDictionaryValue) {
      return Object.keys(obj as Record<string, unknown>).reduce((acc, key) => {
        acc[key] = toPascalCase((obj as Record<string, unknown>)[key], false);
        return acc;
      }, {} as Record<string, unknown>);
    }
    return Object.keys(obj as Record<string, unknown>).reduce((acc, key) => {
      const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
      const isDict = DICTIONARY_PROPERTIES_PASCAL.has(pascalKey);
      acc[pascalKey] = toPascalCase((obj as Record<string, unknown>)[key], isDict);
      return acc;
    }, {} as Record<string, unknown>);
  }
  return obj;
}

// Convert PascalCase responses → camelCase
api.interceptors.response.use(
  response => {
    if (response.data && typeof response.data === 'object') {
      response.data = toCamelCase(response.data);
    }
    return response;
  },
  error => {
    console.error('Admin API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
      data: error.response?.data,
    });
    // Clear auth state on 401 so the app returns to the sign-in surface.
    if (error.response?.status === 401) {
      notifyUnauthorized();
    }
    return Promise.reject(error);
  }
);

// Convert camelCase request bodies → PascalCase
api.interceptors.request.use(config => {
  if (config.data && typeof config.data === 'object') {
    config.data = toPascalCase(config.data);
  }
  // Inject the CSRF header on state-changing requests (X-CSRF-Token must equal
  // the session's csrf_token claim). Prefer the in-memory token from
  // /connect/session so it works cross-host.
  const method = (config.method || 'get').toLowerCase();
  if (MUTATING_METHODS.has(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers.set('X-CSRF-Token', csrf);
    }
  }
  // Forward dev-mode identity headers (no-op outside dev mode).
  const devHeaders = getDevIdentityHeaders();
  for (const [key, value] of Object.entries(devHeaders)) {
    config.headers.set(key, value);
  }
  return config;
});

/** Query string shared by the four usage-analytics cuts, which take identical parameters. */
function usageRangeQuery({ fromDate, toDate }: UsageAnalyticsRangeParams): string {
  return new URLSearchParams({ fromDate, toDate }).toString();
}

// Types
export interface AdminDashboardSummary {
  totalSubscriptions: number;
  activeSubscriptions: number;
  suspendedSubscriptions: number;
  pendingSubscriptions: number;
  cancelledSubscriptions: number;
  totalSeats: number;
  monthlyRecurringRevenue: number;
  generatedAt: string;
}

export interface AdminSubscription {
  id: string;
  marketplaceSubscriptionId: string;
  organizationId: string | null;
  offerId: string;
  planId: string;
  status: string;
  quantity: number;
  beneficiaryEmail: string | null;
  beneficiaryTenantId: string | null;
  purchaserEmail: string | null;
  termStartDate: string | null;
  termEndDate: string | null;
  createdAt: string;
  activatedAt: string | null;
  suspendedAt: string | null;
  cancelledAt: string | null;
  tokensReportedThisPeriod: number;
  lastMeteringEventTime: string | null;
  adminNotes: string | null;
  suspensionReason: string | null;
  employeeTemplateId: string | null;
  autoRenew: boolean;
  updatedAt: string;
  monthlyRevenue: number;
}

export interface SubscriptionListResponse {
  subscriptions: AdminSubscription[];
  continuationToken: string | null;
  hasMore: boolean;
}

export interface UsageHistory {
  subscriptionId: string;
  tokensReportedThisPeriod: number;
  lastMeteringEventTime: string | null;
  currentSeats: number;
  usageEvents: UsageEvent[];
}

export interface UsageEvent {
  dimension: string;
  quantity: number;
  timestamp: string;
  status: string;
}

// Organization Types
export interface OrganizationSummary {
  totalOrganizations: number;
  activeOrganizations: number;
  suspendedOrganizations: number;
  trialOrganizations: number;
  totalSubscribedEmployees: number;
  generatedAt: string;
}

export interface AdminOrganization {
  id: string;
  name: string;
  displayName: string | null;
  m365TenantId: string;
  connectionType: string;
  status: string;
  subscribedEmployeeCount: number;
  subscribedEmployeeIds: string[];
  currentMonthSpend: number;
  monthlySpendLimit: number;
  billingEmail: string | null;
  stripeCustomerId: string | null;
  allowedDomains: string[];
  createdAt: string;
  suspendedAt: string | null;
  suspensionReason: string | null;
  settings: AdminOrganizationSettings;
  productRatePlanIds: Record<string, string>;
  tokenBalance: number | null;
  totalTokensGranted: number | null;
  totalTokensConsumed: number | null;
  // Only Trialing product subscriptions, stable order. Optional — absent/empty on orgs with no
  // active trials; the org-list Status cell guards for undefined before rendering trial badges.
  productTrials?: { productId: string; trialEndsAt: string | null }[];
  // Admin-created organizations (spec §6.1 / §6.5). Optional because an API that predates the
  // feature omits them; the detail-page badges key on explicit true / false, never on "missing".
  customerTermsPending?: boolean;
  /** Null on the list response, where m365 does not compute it; set on detail and create. */
  ownershipEstablished?: boolean | null;
  createdByStaffAccountId?: string | null;
}

export interface AdminOrganizationSettings {
  enableVoiceCalls: boolean;
  enableSMS: boolean;
  enableTeamsIntegration: boolean;
  requireApprovalForHighRiskActions: boolean;
  conversationRetentionDays: number;
  defaultTimeZone: string | null;
  sharePointConsentStatus: string | null;
}

export interface OrganizationListResponse {
  organizations: AdminOrganization[];
  totalCount: number;
  skip: number;
  take: number;
}

export interface AdminUser {
  id: string;
  organizationId: string;
  email: string;
  name: string | null;
  m365UserPrincipalName: string | null;
  aadObjectId: string;
  role: string;
  status: string;
  m365ConsentStatus: string;
  createdAt: string;
  lastLoginAt: string;
  /** Trainer product-line limit (spec 2026-10-29 §4.7). null/absent = All. */
  productLineScope?: ProductLineScope | null;
  productLineScopeVersion?: number;
}

export interface ProductLineScope {
  /** 'malformed' is read-only: the API reports a stored value it cannot read (access is denied until fixed). */
  mode: 'all' | 'limited' | 'malformed';
  lineIds: string[] | null;
}

export interface AdminProductLine {
  id: string;
  name: string;
  parentId: string | null;
  archived: boolean;
}

export interface AdminProductLinesResponse {
  available: boolean;
  topLabel: string;
  lines: AdminProductLine[];
}

export interface UserListResponse {
  users: AdminUser[];
  totalCount: number;
  skip: number;
  take: number;
}

// Org feedback (Feedback tab). Mirrors the admin feedback list projection
// (AdminFeedbackListItem in m365dotnet) — reporterEmail is the field name the
// backend DTO actually uses (confirmed against AdminFeedbackModels.cs).
export interface OrgFeedbackItem {
  id: string;
  feedbackKind: string | null;
  type: string | null;
  description: string | null;
  reporterEmail: string | null;
  status: string;
  createdAt: string;
}

export interface OrgFeedbackListResponse {
  items: OrgFeedbackItem[];
  continuationToken?: string;
}

export interface OrganizationSubscription {
  id: string;
  marketplaceSubscriptionId: string;
  offerId: string;
  planId: string;
  status: string;
  quantity: number;
  termStartDate: string | null;
  termEndDate: string | null;
  createdAt: string;
  activatedAt: string | null;
  // Note: purchaserEmail, beneficiaryEmail, employeeTemplateId, adminNotes
  // are available on AdminSubscription (full detail) but not populated by
  // the org-scoped GetOrganizationSubscriptions endpoint's .Select() mapping.
}

export type OrganizationOnboardingStepKey =
  | 'owner_logged_in'
  | 'rubric_created'
  | 'calls_uploaded'
  | 'calls_scored';

export interface OrganizationUsageSummaryUsage {
  tokenBalance: number | null;
  totalTokensUsed: number | null;
  coreChats: number | null;
  studioProjects: number | null;
  daysIdle: number | null;
  lastActivityAt: string | null;
}

export interface OrganizationOnboardingStep {
  key: OrganizationOnboardingStepKey;
  label: string;
  completed: boolean;
  completedAt: string | null;
  count: number | null;
}

export interface OrganizationUsageSummaryOnboarding {
  available: boolean;
  error: string | null;
  steps: OrganizationOnboardingStep[];
  completedCount: number;
  totalCount: number;
}

export interface OrganizationUsageSummary {
  organizationId: string;
  generatedAt: string;
  usage: OrganizationUsageSummaryUsage;
  onboarding: OrganizationUsageSummaryOnboarding;
}

// Token-spend attribution (Token Usage tab). Mirrors the AdminUsageAnalyticsController
// DTOs in m365dotnet — the wire is PascalCase, the response interceptor camelCases the
// keys, so these are the camelCased shapes the components actually receive.

/**
 * Echo of the requested window plus the org's real data-begins boundary. `oldestRecordUtc`
 * is the server timestamp of the oldest retained usage row (null when the org has none) —
 * it is NOT derived from the container TTL, which is not active in production.
 */
export interface UsageAnalyticsRange {
  fromUtc: string;
  /** Exclusive end of the requested window. */
  toUtc: string;
  oldestRecordUtc: string | null;
}

export interface UsageSourceProjectDayBucket {
  /** UTC day, YYYY-MM-DD. Bucketed on the server ingest timestamp, never occurredAt. */
  dateUtc: string;
  sourceProject: string;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  requestCount: number;
}

export interface UsageBySourceProjectResponse {
  range: UsageAnalyticsRange;
  buckets: UsageSourceProjectDayBucket[];
  totalTokens: number;
}

/**
 * `userId` is passed through exactly as recorded. Unattributed sentinels — the
 * `salestrainer-system` lump id and gateway service-principal actor ids — arrive
 * untranslated; labelling them is this dashboard's job (see tokenUsageAttribution).
 */
export interface UsageByUserTotals {
  userId: string;
  totalTokens: number;
  successTokens: number;
  failedTokens: number;
  successCount: number;
  failedCount: number;
}

export interface UsageByUserResponse {
  range: UsageAnalyticsRange;
  users: UsageByUserTotals[];
  totalTokens: number;
}

export interface UsageByActionTotals {
  /** Null for rows recorded without an action name. Deliberately not dropped by the server. */
  actionName: string | null;
  totalTokens: number;
  successTokens: number;
  failedTokens: number;
  successCount: number;
  failedCount: number;
}

export interface UsageByActionResponse {
  range: UsageAnalyticsRange;
  actions: UsageByActionTotals[];
  totalTokens: number;
}

export interface UsageSessionTotals {
  sessionId: string;
  totalTokens: number;
  requestCount: number;
  /** Null when no row in the session carried a duration. */
  durationSeconds: number | null;
  /** Null whenever the duration is absent or non-positive — never a substitute rate. */
  tokensPerHour: number | null;
}

/**
 * Per-hour figures are computed over duration-bearing sessions ONLY; duration-less
 * sessions are reported separately so the rate is never diluted.
 */
export interface UsageSessionPricingStats {
  sessionCount: number;
  sessionsWithDurationCount: number;
  sessionsWithoutDurationCount: number;
  totalTokens: number;
  averageTokensPerSession: number;
  tokensInSessionsWithDuration: number;
  tokensInSessionsWithoutDuration: number;
  totalDurationSeconds: number;
  averageTokensPerHour: number | null;
}

export interface UsageBySessionResponse {
  range: UsageAnalyticsRange;
  sessions: UsageSessionTotals[];
  pricingStats: UsageSessionPricingStats;
}

/**
 * Half-open UTC window. `toDate` is EXCLUSIVE and the server rejects a span over 90 days
 * with a 400 — see resolveTokenUsageRange, which is the only thing that should build these.
 */
export interface UsageAnalyticsRangeParams {
  /** Inclusive start, YYYY-MM-DD. */
  fromDate: string;
  /** Exclusive end, YYYY-MM-DD. */
  toDate: string;
}

export interface UpdateOrganizationRequest {
  displayName?: string;
  billingEmail?: string;
  monthlySpendLimit?: number;
  allowedDomains?: string[];
  stripeCustomerId?: string;
  settings?: {
    enableVoiceCalls?: boolean;
    enableSMS?: boolean;
    enableTeamsIntegration?: boolean;
    requireApprovalForHighRiskActions?: boolean;
    defaultTimeZone?: string;
  };
}

// Admin-created organizations (docs/superpowers/specs/2026-10-20-admin-precreated-organizations-design.md §6)
export type OrganizationIdentityProviderKind = 'Microsoft365' | 'GoogleWorkspace' | 'None';

export interface CreateOrganizationRequest {
  /** Per-submit idempotency key minted by the dashboard; a replay returns the org created first. */
  requestId: string;
  name: string;
  displayName: string;
  identityProvider: OrganizationIdentityProviderKind;
  workEmailDomain: string;
  billingEmail: string;
}

export type InvitationRole = 'Owner' | 'Admin' | 'Member';
export type StaffMemberRole = 'Admin' | 'Member';

export interface AddStaffMemberRequest {
  /** The staff account id (AdminUser.id from the org_button_up users listing). */
  accountId: string;
  role: StaffMemberRole;
}

export interface CreateInvitationRequest {
  email: string;
  role: InvitationRole;
}

export interface AdminInvitation {
  id: string;
  email: string;
  role: string;
  /** Pending | Accepted | Expired | Revoked | AutoResolved */
  status: string;
  expiresAt: string;
  /** Present for pending invitations only; shown to staff for the copy-link action. */
  acceptUrl: string | null;
  lastSentAt: string | null;
  sendCount: number;
}

// Rate Plan Types
export interface RatePlan {
  id: string;
  productId: string;
  name: string;
  description: string | null;
  billingInterval: string;
  basePrice: number;
  currency: string;
  includedTokens: number;
  overageRatePerThousand: number;
  stripePriceId: string | null;
  status: string;
  // Optional: the backend omits CoveredProductIds for some plans, so the camelCased
  // response may not carry this field. Callers must default to [] before accessing it.
  coveredProductIds?: string[];
  assignedOrganizationCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRatePlanRequest {
  productId: string;
  name: string;
  description?: string;
  basePrice: number;
  includedTokens: number;
  overageRatePerThousand: number;
  stripePriceId?: string;
  billingInterval?: string;
}

export interface UpdateRatePlanRequest {
  name?: string;
  description?: string;
  basePrice?: number;
  includedTokens?: number;
  overageRatePerThousand?: number;
  stripePriceId?: string;
}

// Entitlement Types
export interface ProductEntitlement {
  id: string;
  organizationId: string;
  productId: string;
  enabled: boolean;
  enabledAt: string | null;
  enabledBy: string | null;
  disabledAt: string | null;
  disabledBy: string | null;
  tier: string;
  settings: Record<string, unknown> | null;
  quotas: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, unknown> | null;
}

export interface OrganizationEntitlementSummary {
  organizationId: string;
  entitlements: ProductEntitlement[];
  enabledProducts: string[];
  generatedAt: string;
}

export interface UpdateEntitlementRequest {
  tier?: string;
  settings?: Record<string, unknown>;
  quotas?: Record<string, unknown>;
}

// Invoice Types
export interface InvoiceLineItem {
  id: string;
  productCode: string;
  description: string;
  quantity: number;
  unitAmount: number;
  totalAmount: number;
  isMeteredUsage: boolean;
  ratePlanId: string | null;
}

export interface InternalInvoice {
  id: string;
  organizationId: string;
  number: string;
  status: string;
  billingPeriod: string;
  billingChannel: string;
  channelReferenceId: string | null;
  channelPaymentUrl: string | null;
  lineItems: InvoiceLineItem[];
  subtotal: number;
  credits: number;
  totalAmount: number;
  currency: string;
  createdAt: string;
  finalizedAt: string | null;
  submittedAt: string | null;
  paidAt: string | null;
  erpExportStatus: string;
  notes: string | null;
  failureReason: string | null;
  paymentIntentId: string | null;
}

export interface InvoiceListResponse {
  invoices: InternalInvoice[];
  count: number;
  skip: number;
  limit: number;
}

export interface StripeSyncResponse {
  organizationsScanned: number;
  invoicesCreated: number;
  invoicesSkipped: number;
  errors: string[];
  syncedAt: string;
}

export interface ReconciliationFlag {
  organizationId: string;
  organizationName: string | null;
  flagType: string;
  description: string;
  severity: string;
  stripeInvoiceId: string | null;
  internalInvoiceId: string | null;
}

export interface ReconciliationReport {
  generatedAt: string;
  organizationsChecked: number;
  flagsFound: number;
  flags: ReconciliationFlag[];
}

// Billing Summary Types
export interface OrgBillingSummary {
  organizationId: string;
  billingPeriod: string;
  generatedAt: string;
  currency: string;

  totalAmount: number;
  subscriptionTotal: number;
  usageTotal: number;
  credits: number;
  budgetLimit: number;

  stripeCustomerId: string | null;
  ratePlanId: string | null;
  ratePlanName: string | null;

  products: ProductBillingEntry[];
  employees: EmployeeBillingEntry[];
  invoices: BillingInvoiceSummary[];
}

// ---- Paid-products portal request/response shapes (plan U7) ----

/** Collection mode for a deal: monthly billing (default) or full-term prepay. Lowercase on the wire
 * both directions — the axios interceptor camelCases KEYS, not VALUES. */
export type DealCollectionMode = 'monthly' | 'prepaid';

export interface AdminCreateDealRequest {
  productId: string;
  monthlyPriceUsd: number;
  monthlyTokenAllotment: number;
  trialLengthDays: number;
  /**
   * Term length in whole months (default 1 server-side). A default one-month deal omits this and
   * behaves exactly as today. There is NO lump-sum field — the prepay total is always computed
   * server-side as monthlyPriceUsd × termMonths.
   */
  termMonths?: number;
  /**
   * "Monthly" (default) bills the card on the anniversary; "Prepaid" charges the full term once at
   * creation. Omitted → server defaults to Monthly, so existing callers are unaffected.
   */
  collectionMode?: DealCollectionMode;
  /** Stable per-submit token so a retried submit cannot double-charge. */
  requestIdempotencyToken?: string;
  /** Optional reason (e.g. a $0 giveaway justification) recorded on the deal-authorship audit row. */
  reason?: string;
}

export interface AdminCreateDealResponse {
  organizationId: string;
  productId: string;
  /** TrialStarted | ChargedAndActivated | AlreadyActive (the 2xx outcomes). */
  outcome: string;
  subscriptionId: string | null;
  latestStripePaymentIntentId: string | null;
  monthlyPriceUsd: number;
  monthlyTokenAllotment: number;
  trialEndsAt: string | null;
  /** True when a synchronous charge succeeded at deal-create (0-day deal). */
  charged: boolean;
  /**
   * The ACTUAL total charged to the card at creation, to the cent: for a prepaid deal this is the
   * full-term lump sum, for a 0-day monthly deal it is one month, and it is null/0 when nothing was
   * charged (trial or $0 giveaway). The success panel shows exactly this figure.
   */
  chargedAmountUsd?: number | null;
  /** Echoed term length (null for a default one-month deal). */
  termMonths?: number | null;
  /** Echoed collection mode. */
  collectionMode?: DealCollectionMode | null;
  /** ISO committed term-end date (null when not a term deal). */
  committedThroughDate?: string | null;
}

export interface AdminCardCollectionResponse {
  url: string;
}

export interface GrantHistoryItem {
  amount: number;
  balanceAfter: number;
  source: string;
  reason: string | null;
  status: string | null;
  idempotencyKey: string | null;
  createdAt: string;
}

export interface GrantHistoryResponse {
  organizationId: string;
  grants: GrantHistoryItem[];
}

export interface GrantTokensResponse {
  organizationId: string;
  tokensGranted: number;
  amountCreditedCents: number;
  stripeTransactionId: string | null;
  idempotencyKey: string;
}

// Read-only per-org paid-product subscription row (staff-only). Backend returns PascalCase; the
// axios interceptor camelCases it to this shape. `status` is tolerated as an open string set —
// render code must never crash/blank on an unknown value.
export interface AdminProductSubscription {
  id: string;
  productId: string;
  status: string;
  monthlyPriceUsd: number;
  monthlyTokenAllotment: number;
  startedAt: string;
  trialEndsAt: string | null;
  nextBillingDate: string | null;
  cancelledAt: string | null;
  enabledByMethod: string | null;
  stripeCustomerId: string | null;
  // ---- Term-deal fields (all nullable; pre-feature rows deserialize to null → row renders as a
  // plain monthly deal with no term text, no badge, no changed wording). ----
  /** Committed term length in whole months. Null / 1 → a plain monthly deal (no term display). */
  termMonths?: number | null;
  /** "monthly" or "prepaid". Null → treated as monthly. */
  collectionMode?: DealCollectionMode | null;
  /** ISO committed term-end date (the "through {date}" shown on the row). */
  committedThroughDate?: string | null;
}

export interface ProductBillingEntry {
  productCode: string;
  productName: string;
  category: 'subscription' | 'usage';
  totalAmount: number;

  quantity?: number;
  unitPrice?: number;
  marketplaceSubscriptionId?: string;
  marketplacePlanId?: string;

  totalConsumption?: number;
  includedAmount?: number;
  overageAmount?: number;
  unitRate?: number;
  unitLabel?: string;

  employeeBreakdown: ProductEmployeeEntry[];
}

export interface ProductEmployeeEntry {
  employeeId: string;
  employeeName: string;
  status?: string;
  cost: number;
  consumption?: number;
  percentOfTotal?: number;
}

export interface EmployeeBillingEntry {
  employeeId: string;
  employeeName: string;
  totalCost: number;
  productBreakdown: EmployeeProductEntry[];
}

export interface EmployeeProductEntry {
  productCode: string;
  productName: string;
  category: 'subscription' | 'usage';
  cost: number;
  detail?: string;
}

export interface BillingInvoiceSummary {
  id: string;
  number: string;
  billingPeriod: string;
  totalAmount: number;
  status: string;
  paidAt?: string;
  channelReferenceId: string;
  channelPaymentUrl?: string;
}

export interface CreateInvoiceRequest {
  organizationId: string;
  billingPeriod?: string;
  notes?: string;
  submitToStripe: boolean;
  lineItems: CreateInvoiceLineItemRequest[];
}

export interface CreateInvoiceLineItemRequest {
  productCode: string;
  description: string;
  quantity: number;
  unitAmount: number;
}

// Employee Admin Types
export interface AdminEmployee {
  id: string;
  name: string;
  preferredName?: string;
  employeeStatus: string;
  sourceTemplateId?: string;
  typeId: string;
  subscription?: AdminEmployeeSubscription;
}

export interface AdminEmployeeSubscription {
  subscriptionId: string;
  priceId: string;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

export interface UpdateEmployeeSubscriptionRequest {
  subscriptionId?: string;
  priceId?: string;
  status?: string;
}

// ==================== Platform Signups types ====================

/** Segment a signup belongs to. Mirrors the backend snapshot classification. */
export type SignupSegment = 'real' | 'demo' | 'orphan';

/** Studio data freshness flags returned alongside a paged signups response. */
export interface PlatformSignupsDegradedFlags {
  /** True when Studio is unreachable or returned a Studio-level failure. */
  studio: boolean;
  /** True when Studio responded but some orgs were omitted (partial data). */
  studioPartial: boolean;
}

/** One organization row (Real orgs / Demo & internal tabs). */
export interface PlatformSignupOrgRow {
  orgId: string;
  orgName: string;
  /** Null when the owner-account lookup fails. */
  ownerName: string | null;
  /** Null when the owner-account lookup fails. */
  ownerEmail: string | null;
  /** ISO 8601 UTC timestamp. */
  signupDate: string;
  /** ISO 8601 UTC timestamp. Null when org has no recorded activity. */
  lastActivityDate: string | null;
  /** Null when org has no recorded activity. */
  daysSinceLastActivity: number | null;
  /** Lifetime tokens consumed. Null when org has no token pool. */
  tokensUsed: number | null;
  coreChatCount: number;
  /** ISO 8601 UTC timestamp of last Parslee Core chat. Null when no chats. */
  lastChatAt: string | null;
  /** Null when Studio data is unavailable for this org. */
  studioProjectCount: number | null;
  /** ISO 8601 UTC timestamp. Null when Studio data is unavailable. */
  studioLastProjectAt: string | null;
  isTestName: boolean;
  isInternalEmail: boolean;
  isDemoOrg: boolean;
}

/** One orphan account row (Orphan accounts tab). */
export interface PlatformOrphanAccountRow {
  accountId: string;
  name: string | null;
  email: string;
  /** ISO 8601 UTC timestamp. */
  signupDate: string;
}

/** A single page-slice row is one of the two shapes depending on segment. */
export type PlatformSignupsPageRow = PlatformSignupOrgRow | PlatformOrphanAccountRow;

/** Response from GET /admin/platform-signups/page. */
export interface PlatformSignupsPageResponse {
  /** ISO 8601 UTC timestamp marking when the underlying snapshot was built. */
  generatedAt: string;
  degraded: PlatformSignupsDegradedFlags;
  rows: PlatformSignupsPageRow[];
  /** Count of rows matching the active filters BEFORE slicing. */
  totalCount: number;
  skip: number;
  take: number;
}

/** One lightweight projection row used for KPI cards + the chart. */
export interface PlatformSignupsProjectionRow {
  /** ISO 8601 UTC timestamp. */
  signupDate: string;
  segment: SignupSegment;
}

/** Response from GET /admin/platform-signups/projection. */
export interface PlatformSignupsProjectionResponse {
  generatedAt: string;
  rows: PlatformSignupsProjectionRow[];
}

/** Query inputs for the paged slice endpoint. */
export interface PlatformSignupsPageParams {
  segment: SignupSegment;
  search?: string;
  sortField?: string;
  sortDir?: 'asc' | 'desc';
  skip?: number;
  take?: number;
  /** ISO 8601 instant — pre-resolved by the client (no server tz math). */
  rangeStart?: string;
  /** ISO 8601 instant — exclusive upper bound. */
  rangeEnd?: string;
  /** When true, returns ALL filtered rows (skip/take ignored) for CSV export. */
  export?: boolean;
  /** When true, forces a fresh snapshot build (Refresh button path). */
  refresh?: boolean;
}

export const adminApi = {
  /**
   * Fetch admin dashboard summary with key metrics
   */
  getSummary: async (): Promise<AdminDashboardSummary> => {
    const response = await api.get<AdminDashboardSummary>('/admin/marketplace/summary');
    return response.data;
  },

  /**
   * List all marketplace subscriptions with optional filtering
   */
  listSubscriptions: async (
    status?: string,
    search?: string,
    pageSize: number = 50,
    continuationToken?: string
  ): Promise<SubscriptionListResponse> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    params.append('pageSize', pageSize.toString());
    if (continuationToken) params.append('continuationToken', continuationToken);

    const response = await api.get<SubscriptionListResponse>(
      `/admin/marketplace/subscriptions?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get a single subscription by ID
   */
  getSubscription: async (subscriptionId: string): Promise<AdminSubscription> => {
    const response = await api.get<AdminSubscription>(
      `/admin/marketplace/subscriptions/${subscriptionId}`
    );
    return response.data;
  },

  /**
   * Suspend a subscription
   */
  suspendSubscription: async (subscriptionId: string, reason: string): Promise<void> => {
    await api.post(
      `/admin/marketplace/subscriptions/${subscriptionId}/suspend`,
      { reason }
    );
  },

  /**
   * Reinstate a suspended subscription
   */
  reinstateSubscription: async (subscriptionId: string): Promise<void> => {
    await api.post(
      `/admin/marketplace/subscriptions/${subscriptionId}/reinstate`,
      {}
    );
  },

  /**
   * Update subscription notes
   */
  updateNotes: async (subscriptionId: string, notes: string): Promise<void> => {
    await api.patch(
      `/admin/marketplace/subscriptions/${subscriptionId}/notes`,
      { notes }
    );
  },

  /**
   * Get usage history for a subscription
   */
  getUsageHistory: async (
    subscriptionId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<UsageHistory> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const response = await api.get<UsageHistory>(
      `/admin/marketplace/subscriptions/${subscriptionId}/usage?${params.toString()}`
    );
    return response.data;
  },

  // ==================== Organization Admin APIs ====================

  /**
   * Fetch organization summary with key metrics
   */
  getOrganizationSummary: async (): Promise<OrganizationSummary> => {
    const response = await api.get<OrganizationSummary>('/admin/organizations/summary');
    return response.data;
  },

  /**
   * List all organizations with optional filtering
   */
  listOrganizations: async (
    status?: string,
    search?: string,
    skip: number = 0,
    take: number = 50
  ): Promise<OrganizationListResponse> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    params.append('skip', skip.toString());
    params.append('take', take.toString());

    const response = await api.get<OrganizationListResponse>(
      `/admin/organizations?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get a single organization by ID
   */
  getOrganization: async (organizationId: string): Promise<AdminOrganization> => {
    const response = await api.get<AdminOrganization>(
      `/admin/organizations/${organizationId}`
    );
    return response.data;
  },

  /**
   * Fetch the per-org onboarding checklist and usage metrics for the org detail Overview card.
   */
  getOrganizationUsageSummary: async (
    organizationId: string
  ): Promise<OrganizationUsageSummary> => {
    const response = await api.get<OrganizationUsageSummary>(
      `/admin/organizations/${organizationId}/usage-summary`
    );
    return response.data;
  },

  /**
   * Recorded token spend per UTC day per source project, for the Token Usage tab's chart.
   * Consumes GET /admin/organizations/{orgId}/usage/by-source-project (U6, RequireParsleeStaff —
   * the dashboard's existing session auth already satisfies it, no apiKey parameter).
   */
  getOrgUsageBySourceProject: async (
    organizationId: string,
    range: UsageAnalyticsRangeParams
  ): Promise<UsageBySourceProjectResponse> => {
    const response = await api.get<UsageBySourceProjectResponse>(
      `/admin/organizations/${organizationId}/usage/by-source-project?${usageRangeQuery(range)}`
    );
    return response.data;
  },

  /** Recorded spend per user over the range, with the success/failure split. */
  getOrgUsageByUser: async (
    organizationId: string,
    range: UsageAnalyticsRangeParams
  ): Promise<UsageByUserResponse> => {
    const response = await api.get<UsageByUserResponse>(
      `/admin/organizations/${organizationId}/usage/by-user?${usageRangeQuery(range)}`
    );
    return response.data;
  },

  /** Recorded spend per action name over the range. Rows with no action name arrive as null. */
  getOrgUsageByAction: async (
    organizationId: string,
    range: UsageAnalyticsRangeParams
  ): Promise<UsageByActionResponse> => {
    const response = await api.get<UsageByActionResponse>(
      `/admin/organizations/${organizationId}/usage/by-action?${usageRangeQuery(range)}`
    );
    return response.data;
  },

  /** Per-session spend plus the tokens-per-session / tokens-per-hour pricing inputs. */
  getOrgUsageBySession: async (
    organizationId: string,
    range: UsageAnalyticsRangeParams
  ): Promise<UsageBySessionResponse> => {
    const response = await api.get<UsageBySessionResponse>(
      `/admin/organizations/${organizationId}/usage/by-session?${usageRangeQuery(range)}`
    );
    return response.data;
  },

  /**
   * Create an organization before any customer has signed in (spec §6.1). `requestId` is the
   * per-submit idempotency key; the dialog keeps it across a 502 / network retry and across a
   * 409 request_in_progress (another create with this key or this domain is still running, so the
   * dialog waits and retries with the same key), and mints a new one after any other 4xx. 201 on
   * create; 200 on a replay or when a retry completes a committed create (both resolve here).
   * Throws on non-2xx: 409 organization_exists (workEmailDomain, matches in the body), 409
   * request_in_progress (requestId or domain), 422 domain_not_microsoft_tenant, 502
   * tenant_lookup_failed, 500 organization_create_failed (requestId, plus organizationId when the
   * write committed), 401 account_context_missing. 400 request_body_required, request_id_required,
   * request_id_invalid, name_required, identity_provider_required / _invalid,
   * work_email_domain_invalid and billing_email_invalid also exist; the dashboard validates or
   * mints those fields, so they take the generic error path. Error bodies are camelCase and are
   * not converted by the interceptor; a 404 body may be a prose string rather than a code.
   */
  createOrganization: async (
    data: Omit<CreateOrganizationRequest, 'requestId'>,
    requestId: string = crypto.randomUUID()
  ): Promise<AdminOrganization> => {
    const response = await api.post<AdminOrganization>('/admin/organizations', {
      ...data,
      requestId,
    });
    return response.data;
  },

  /**
   * Update organization settings
   */
  updateOrganization: async (
    organizationId: string,
    data: UpdateOrganizationRequest
  ): Promise<AdminOrganization> => {
    const response = await api.put<AdminOrganization>(
      `/admin/organizations/${organizationId}`,
      data
    );
    return response.data;
  },

  /**
   * Suspend an organization
   */
  suspendOrganization: async (organizationId: string, reason: string): Promise<void> => {
    await api.post(
      `/admin/organizations/${organizationId}/suspend`,
      { reason }
    );
  },

  /**
   * Reactivate a suspended organization
   */
  reactivateOrganization: async (organizationId: string): Promise<void> => {
    await api.post(
      `/admin/organizations/${organizationId}/reactivate`,
      {}
    );
  },

  /**
   * List users in an organization
   */
  getOrganizationUsers: async (
    organizationId: string,
    status?: string,
    skip: number = 0,
    take: number = 50
  ): Promise<UserListResponse> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('skip', skip.toString());
    params.append('take', take.toString());

    const response = await api.get<UserListResponse>(
      `/admin/organizations/${organizationId}/users?${params.toString()}`
    );
    return response.data;
  },

  /**
   * List all feedback (thumbs up/down, bug reports, feature requests) for an
   * organization. Consumes GET /admin/organizations/{orgId}/feedback (U8) —
   * telemetry-inclusive by design (this is the one surface that must show it).
   */
  listOrgFeedback: async (
    organizationId: string,
    options: { status?: string; continuationToken?: string; limit?: number } = {}
  ): Promise<OrgFeedbackListResponse> => {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.continuationToken) params.append('continuationToken', options.continuationToken);
    params.append('limit', (options.limit ?? 50).toString());

    const response = await api.get<OrgFeedbackListResponse>(
      `/admin/organizations/${organizationId}/feedback?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get a single user by ID
   */
  getUser: async (organizationId: string, userId: string): Promise<AdminUser> => {
    const response = await api.get<AdminUser>(
      `/admin/organizations/${organizationId}/users/${userId}`
    );
    return response.data;
  },

  /**
   * Update user role
   */
  updateUserRole: async (
    organizationId: string,
    userId: string,
    role: string
  ): Promise<AdminUser> => {
    const response = await api.patch<AdminUser>(
      `/admin/organizations/${organizationId}/users/${userId}/role`,
      { role }
    );
    return response.data;
  },

  /** The org's Trainer product lines (archived included) for the member limit picker. */
  getTrainerProductLines: async (organizationId: string): Promise<AdminProductLinesResponse> => {
    const response = await api.get<AdminProductLinesResponse>(
      `/admin/organizations/${organizationId}/salestrainer/product-lines`
    );
    return response.data;
  },

  /**
   * Limit a member to chosen product lines, or clear the limit. 400 invalid scope; 404 not a member; 409
   * product_line_scope_version_conflict when productLineScopeVersion is sent and the stored version differs.
   */
  updateUserProductLineScope: async (
    organizationId: string,
    userId: string,
    scope: { mode: 'all' | 'limited'; lineIds: string[] | null; productLineScopeVersion?: number }
  ): Promise<AdminUser> => {
    const response = await api.put<AdminUser>(
      `/admin/organizations/${organizationId}/users/${userId}/product-line-scope`,
      scope
    );
    return response.data;
  },

  /**
   * Suspend a user
   */
  suspendUser: async (organizationId: string, userId: string): Promise<void> => {
    await api.post(
      `/admin/organizations/${organizationId}/users/${userId}/suspend`,
      {}
    );
  },

  /**
   * Reactivate a suspended user
   */
  reactivateUser: async (organizationId: string, userId: string): Promise<void> => {
    await api.post(
      `/admin/organizations/${organizationId}/users/${userId}/reactivate`,
      {}
    );
  },

  /**
   * Delete a user (soft delete)
   */
  deleteUser: async (organizationId: string, userId: string): Promise<void> => {
    await api.delete(
      `/admin/organizations/${organizationId}/users/${userId}`
    );
  },

  /**
   * Add an existing ButtonUp staff account as a member, immediately and without email (spec §6.2).
   * 403 staff_only when the account is not staff; 409 membership_exists when a membership already
   * exists; 400 account_id_required / invalid_role; 404 when the org is missing.
   */
  addStaffMember: async (
    organizationId: string,
    data: AddStaffMemberRequest
  ): Promise<AdminUser> => {
    const response = await api.post<AdminUser>(
      `/admin/organizations/${organizationId}/staff-members`,
      data
    );
    return response.data;
  },

  /**
   * All invitations for an organization (spec §6.3). Tolerates both a bare array and an
   * `{ invitations: [...] }` envelope so the dashboard does not depend on which the API settles
   * on. A 404 means the API predates the feature; callers hide the panel and its buttons.
   */
  listInvitations: async (organizationId: string): Promise<AdminInvitation[]> => {
    const response = await api.get<
      AdminInvitation[] | { invitations?: AdminInvitation[]; Invitations?: AdminInvitation[] }
    >(`/admin/organizations/${organizationId}/invitations`);
    const body = response.data;
    return Array.isArray(body) ? body : body?.invitations ?? body?.Invitations ?? [];
  },

  /**
   * Create an invitation without sending it (spec §6.3). 409 invitation_pending when a pending
   * invitation for the email already exists; 400 request_body_required / email_invalid /
   * invalid_role.
   */
  createInvitation: async (
    organizationId: string,
    data: CreateInvitationRequest
  ): Promise<AdminInvitation> => {
    const response = await api.post<AdminInvitation>(
      `/admin/organizations/${organizationId}/invitations`,
      data
    );
    return response.data;
  },

  /**
   * Send an invitation (spec §6.3): the first send keeps the current link, every later send
   * rotates it. `requestId` is the per-click idempotency key; the panel keeps it across a 5xx /
   * network failure so a retry cannot send twice. 409 invitation_not_sendable when the invitation
   * is not pending or has expired; 429 send_too_soon (retryAfterSeconds in the body, plus a
   * Retry-After header) under 60 s after the last send; 429 invitation_send_rate_limited (same
   * retryAfterSeconds + Retry-After) under the broader send throttle;
   * 409 send_limit_reached (sendCount, limit) after the tenth send; 409 send_in_progress
   * (invitationId) while another staff member's send for the same invitation runs (the claim for
   * this key is released, so the same key is retried); 409 send_already_processed
   * when the same key is replayed after a send (a success report); 502 invitation_send_failed
   * (requestId) when the email definitely did not go out; 502 invitation_send_outcome_unknown
   * (requestId) when it may have gone out: the server keeps the claim and the per-invitation lock,
   * so new sends answer send_in_progress for up to 1 h; 500 invitation_stamp_failed (emailSent:
   * true, requestId) when the email went out but the record did not update. A 400
   * request_id_invalid also exists but cannot occur from here (the dashboard mints GUIDs); it
   * takes the generic path.
   */
  sendInvitation: async (
    organizationId: string,
    invitationId: string,
    requestId: string = crypto.randomUUID()
  ): Promise<AdminInvitation> => {
    const response = await api.post<AdminInvitation>(
      `/admin/organizations/${organizationId}/invitations/${invitationId}/send`,
      { requestId }
    );
    return response.data;
  },

  /**
   * Revoke a pending invitation (spec §6.3). An expired one is replaced by creating a new one.
   * 409 invitation_not_pending (status in the body); 404 when the invitation is missing.
   */
  revokeInvitation: async (organizationId: string, invitationId: string): Promise<void> => {
    await api.delete(
      `/admin/organizations/${organizationId}/invitations/${invitationId}`
    );
  },

  /**
   * Get subscriptions for an organization
   */
  getOrganizationSubscriptions: async (
    organizationId: string
  ): Promise<{ subscriptions: OrganizationSubscription[] }> => {
    const response = await api.get<{ subscriptions: OrganizationSubscription[] }>(
      `/admin/organizations/${organizationId}/subscriptions`
    );
    return response.data;
  },

  /**
   * Issue a credit to an organization's Stripe account
   */
  issueCredit: async (
    organizationId: string,
    amountCents: number,
    reason: string
  ): Promise<{ success: boolean; transactionId: string | null; newBalanceCents: number }> => {
    const response = await api.post(
      '/admin/billing/credits',
      { organizationId, amountCents, reason }
    );
    return response.data;
  },

  // ==================== Paid-products portal (plan U7) ====================
  // All four methods authenticate via the session cookie + X-CSRF-Token header
  // (injected by the axios interceptor above) — NOT the X-Platform-Api-Key path
  // createInvoice uses (R17). Axios rejects on any non-2xx, so callers surface
  // the specific backend outcome from err.response.data.

  /**
   * Create a custom recurring deal (custom monthly price + token allotment + trial length)
   * on a registered PAID product, delegating to the shared enable saga (plan U3). A 0-day
   * deal charges synchronously at create; a trial deal charges at trial-end; a $0 deal is a
   * giveaway (no card required). Throws on non-2xx so the caller surfaces the outcome
   * (402 collect_card_first / charge_failed, 409 subscription_already_live / enable_in_progress,
   * 400 trial_not_supported).
   */
  createCustomDeal: async (
    organizationId: string,
    deal: AdminCreateDealRequest
  ): Promise<AdminCreateDealResponse> => {
    const response = await api.post<AdminCreateDealResponse>(
      `/admin/organizations/${organizationId}/product-subscriptions`,
      deal
    );
    return response.data;
  },

  /**
   * Open a hosted Stripe setup-mode Checkout session that collects a card on the org's behalf
   * (plan U4). Returns the hosted redirect URL; the caller redirects the browser to it. No new
   * Stripe client deps. `returnPath` MUST be a RELATIVE path starting with "/": the backend
   * builds the Stripe success/cancel URL by appending escaped org/card query params to
   * `{AdminDashboard:Url}{returnPath}` (defaulting to
   * /billing/card-collection-complete when omitted), so callers pass the in-SPA route they want
   * the rep to land back on (e.g. the org's detail page, which re-opens the Billing tab).
   */
  createAdminCardCollectionSession: async (
    organizationId: string,
    returnPath?: string
  ): Promise<AdminCardCollectionResponse> => {
    const response = await api.post<AdminCardCollectionResponse>(
      `/admin/organizations/${organizationId}/card-collection-session`,
      { returnPath }
    );
    return response.data;
  },

  /**
   * Per-org admin-issued grant history for the audit list (plan U5), newest first.
   */
  getGrantHistory: async (
    organizationId: string
  ): Promise<GrantHistoryResponse> => {
    const response = await api.get<GrantHistoryResponse>(
      `/admin/billing/organizations/${organizationId}/grants`
    );
    return response.data;
  },

  /**
   * Read-only paid-product subscriptions for an org (staff-only). Returns all lifecycle states
   * (Trialing / Active / CancelledAtPeriodEnd / Cancelled / Revoked / …) for the Billing tab's
   * Paid Products section.
   */
  getProductSubscriptions: async (organizationId: string): Promise<AdminProductSubscription[]> => {
    const response = await api.get<AdminProductSubscription[]>(`/admin/organizations/${organizationId}/product-subscriptions`);
    return response.data;
  },

  /**
   * Immediately cancel a live (or prepaid pending-cancel) product subscription (staff-only, plan U5).
   * Access and billing end at once; a typed reason is required. Throws on non-2xx so the caller
   * surfaces the outcome by HTTP status — 400 (missing reason), 403 (non-staff), 404 (subscription
   * under a different org), 409 (already terminal — Cancelled/Revoked/Expired; a prepaid
   * CancelledAtPeriodEnd row is NOT terminal and cancels fine). The caller refetches afterward.
   */
  cancelProductSubscriptionNow: async (
    organizationId: string,
    subscriptionId: string,
    reason: string
  ): Promise<void> => {
    await api.post(
      `/admin/organizations/${organizationId}/product-subscriptions/${subscriptionId}/cancel-now`,
      { reason }
    );
  },

  /**
   * Grant bonus tokens to an org's pool (plan U10). Wraps the IssueCredit endpoint sending
   * tokenAmount=<count>, amountCents=0, idempotencyKey=<stable per submit> — NOT amountCents
   * alone (the backend 400s when both tokenAmount<=0 && amountCents<=0). The caller mints ONE
   * idempotency key per submit so a double-click cannot double-grant.
   */
  grantTokens: async (
    organizationId: string,
    grant: { tokenAmount: number; reason: string; idempotencyKey: string }
  ): Promise<GrantTokensResponse> => {
    const response = await api.post<GrantTokensResponse>(
      '/admin/billing/credits',
      {
        organizationId,
        tokenAmount: grant.tokenAmount,
        amountCents: 0,
        reason: grant.reason,
        idempotencyKey: grant.idempotencyKey,
      }
    );
    return response.data;
  },

  // ==================== Rate Plan Admin APIs ====================

  /**
   * List all rate plans with optional filtering by product or status
   */
  listRatePlans: async (
    productId?: string,
    status?: string
  ): Promise<RatePlan[]> => {
    const params = new URLSearchParams();
    if (productId) params.append('productId', productId);
    if (status) params.append('status', status);

    const queryString = params.toString();
    const url = queryString ? `/admin/rate-plans?${queryString}` : '/admin/rate-plans';
    const response = await api.get<RatePlan[]>(url);
    return response.data;
  },

  /**
   * Get a single rate plan by ID
   */
  getRatePlan: async (planId: string): Promise<RatePlan> => {
    const response = await api.get<RatePlan>(
      `/admin/rate-plans/${planId}`
    );
    return response.data;
  },

  /**
   * Create a new rate plan
   */
  createRatePlan: async (data: CreateRatePlanRequest): Promise<RatePlan> => {
    const response = await api.post<RatePlan>(
      '/admin/rate-plans',
      data
    );
    return response.data;
  },

  /**
   * Update an existing rate plan
   */
  updateRatePlan: async (
    planId: string,
    data: UpdateRatePlanRequest
  ): Promise<RatePlan> => {
    const response = await api.put<RatePlan>(
      `/admin/rate-plans/${planId}`,
      data
    );
    return response.data;
  },

  /**
   * Archive a rate plan (soft delete)
   */
  archiveRatePlan: async (planId: string): Promise<void> => {
    await api.post(
      `/admin/rate-plans/${planId}/archive`,
      {}
    );
  },

  /**
   * Assign a rate plan to an organization for a product
   */
  assignRatePlan: async (
    organizationId: string,
    productId: string,
    ratePlanId: string
  ): Promise<void> => {
    await api.post(
      '/admin/rate-plans/assignments',
      { organizationId, productId, ratePlanId }
    );
  },

  /**
   * Remove a rate plan assignment from an organization
   */
  removeRatePlanAssignment: async (
    organizationId: string,
    productId: string
  ): Promise<void> => {
    await api.delete(
      `/admin/rate-plans/assignments/${organizationId}/${productId}`
    );
  },

  // ==================== Entitlement Admin APIs ====================

  /**
   * Get all entitlements for an organization
   */
  getEntitlements: async (
    organizationId: string
  ): Promise<OrganizationEntitlementSummary> => {
    const response = await api.get<OrganizationEntitlementSummary>(
      `/admin/orgs/${organizationId}/entitlements`
    );
    return response.data;
  },

  /**
   * Enable a product for an organization
   */
  enableProduct: async (
    organizationId: string,
    productId: string,
    tier?: string
  ): Promise<ProductEntitlement> => {
    const response = await api.post<ProductEntitlement>(
      `/admin/orgs/${organizationId}/entitlements/${productId}/enable`,
      tier ? { tier } : {}
    );
    return response.data;
  },

  /**
   * Disable a product for an organization
   */
  disableProduct: async (
    organizationId: string,
    productId: string
  ): Promise<ProductEntitlement> => {
    const response = await api.post<ProductEntitlement>(
      `/admin/orgs/${organizationId}/entitlements/${productId}/disable`,
      {}
    );
    return response.data;
  },

  /**
   * Update entitlement settings (tier, settings, quotas) without changing enabled status
   */
  updateEntitlement: async (
    organizationId: string,
    productId: string,
    data: UpdateEntitlementRequest
  ): Promise<ProductEntitlement> => {
    const response = await api.patch<ProductEntitlement>(
      `/admin/orgs/${organizationId}/entitlements/${productId}`,
      data
    );
    return response.data;
  },

  // ==================== Invoice Admin APIs ====================

  /**
   * Get invoices for an organization
   */
  getOrgInvoices: async (
    orgId: string,
    status?: string,
    billingPeriod?: string
  ): Promise<{ invoices: InternalInvoice[] }> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (billingPeriod) params.append('billingPeriod', billingPeriod);

    const queryString = params.toString();
    const url = queryString
      ? `/admin/organizations/${orgId}/invoices?${queryString}`
      : `/admin/organizations/${orgId}/invoices`;
    const response = await api.get<{ invoices: InternalInvoice[] }>(url);
    return response.data;
  },

  /**
   * Get billing summary for an organization (composite endpoint)
   */
  getBillingSummary: async (
    orgId: string,
    period?: string
  ): Promise<OrgBillingSummary> => {
    const params = period ? `?period=${period}` : '';
    const response = await api.get<OrgBillingSummary>(
      `/admin/organizations/${orgId}/billing-summary${params}`
    );
    return response.data;
  },

  /**
   * Sync Stripe invoices into internal invoice store.
   * Uses Platform API key header (X-Platform-Api-Key).
   */
  syncStripeInvoices: async (
    apiKey: string
  ): Promise<StripeSyncResponse> => {
    const response = await api.post<StripeSyncResponse>(
      '/admin/invoices/sync-stripe',
      {},
      { headers: { 'X-Platform-Api-Key': apiKey } }
    );
    return response.data;
  },

  /**
   * Get billing reconciliation report.
   * Uses Platform API key header (X-Platform-Api-Key).
   */
  getReconciliationReport: async (
    apiKey: string
  ): Promise<ReconciliationReport> => {
    const response = await api.get<ReconciliationReport>(
      '/admin/invoices/reconciliation',
      { headers: { 'X-Platform-Api-Key': apiKey } }
    );
    return response.data;
  },

  /**
   * List all invoices globally with filtering.
   * Uses Platform API key header (X-Platform-Api-Key).
   */
  /**
   * Create an à la carte / one-off invoice for an organization.
   * Uses Platform API key header (X-Platform-Api-Key).
   */
  createInvoice: async (
    apiKey: string,
    request: CreateInvoiceRequest
  ): Promise<InternalInvoice> => {
    const response = await api.post<InternalInvoice>(
      '/admin/invoices',
      request,
      { headers: { 'X-Platform-Api-Key': apiKey } }
    );
    return response.data;
  },

  listAllInvoices: async (
    apiKey: string,
    params?: { status?: string; billingPeriod?: string; organizationId?: string; skip?: number; limit?: number }
  ): Promise<InvoiceListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.billingPeriod) searchParams.append('billingPeriod', params.billingPeriod);
    if (params?.organizationId) searchParams.append('organizationId', params.organizationId);
    if (params?.skip !== undefined) searchParams.append('skip', params.skip.toString());
    if (params?.limit !== undefined) searchParams.append('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const url = queryString ? `/admin/invoices?${queryString}` : '/admin/invoices';
    const response = await api.get<InvoiceListResponse>(url, {
      headers: { 'X-Platform-Api-Key': apiKey },
    });
    return response.data;
  },

  // ==================== Employee Admin APIs ====================

  /**
   * List all employees in an organization with subscription state
   */
  getOrganizationEmployees: async (
    organizationId: string
  ): Promise<AdminEmployee[]> => {
    const response = await api.get<AdminEmployee[]>(
      `/admin/organizations/${organizationId}/employees`
    );
    return response.data;
  },

  /**
   * Update or clear an employee's subscription data
   */
  updateEmployeeSubscription: async (
    organizationId: string,
    employeeId: string,
    data: UpdateEmployeeSubscriptionRequest
  ): Promise<void> => {
    await api.put(
      `/admin/organizations/${organizationId}/employees/${employeeId}/subscription`,
      data
    );
  },

  // ==================== Platform Signups APIs ====================

  /**
   * Probe whether the current account may view platform signups. The Signups
   * nav entry is hidden unless this returns true. Fails closed at the call
   * site (any error → not an analyst).
   */
  getMyPlatformAnalyst: async (): Promise<{ isPlatformAnalyst: boolean }> => {
    const response = await api.get<{ isPlatformAnalyst: boolean }>(
      '/accounts/me/platform-analyst'
    );
    return response.data;
  },

  /**
   * Fetch a server-sliced page of signups for the active segment. Search,
   * date range, sort, and pagination are all applied server-side against a
   * short-TTL snapshot; `export: true` returns the full filtered set for CSV.
   */
  getPlatformSignupsPage: async (
    params: PlatformSignupsPageParams
  ): Promise<PlatformSignupsPageResponse> => {
    const q = new URLSearchParams();
    q.append('segment', params.segment);
    if (params.search) q.append('search', params.search);
    if (params.sortField) q.append('sortField', params.sortField);
    if (params.sortDir) q.append('sortDir', params.sortDir);
    if (params.skip !== undefined) q.append('skip', params.skip.toString());
    if (params.take !== undefined) q.append('take', params.take.toString());
    if (params.rangeStart) q.append('rangeStart', params.rangeStart);
    if (params.rangeEnd) q.append('rangeEnd', params.rangeEnd);
    if (params.export) q.append('export', 'true');
    if (params.refresh) q.append('refresh', 'true');

    const response = await api.get<PlatformSignupsPageResponse>(
      `/admin/platform-signups/page?${q.toString()}`
    );
    return response.data;
  },

  /**
   * Fetch the lightweight projection ({ signupDate, segment }) used to compute
   * KPI cards and the signups-over-time chart. Bucketing/grouping happens
   * entirely client-side over this payload — no extra network requests.
   */
  getPlatformSignupsProjection: async (
    refresh?: boolean
  ): Promise<PlatformSignupsProjectionResponse> => {
    const q = new URLSearchParams();
    if (refresh) q.append('refresh', 'true');
    const suffix = q.toString() ? `?${q.toString()}` : '';
    const response = await api.get<PlatformSignupsProjectionResponse>(
      `/admin/platform-signups/projection${suffix}`
    );
    return response.data;
  },
};
